import React, { ComponentType } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { SignUpUserType } from 'features/signUp/types';
import { useCobrand } from 'features/cobrand/hooks/useCobrandUtils';
import { AccessCodeSVG } from './AccessCodeSVG';
import { WelcomeSVG } from './WelcomeSVG';
import { EligibilityCheckSVG } from './EligibilityCheckSVG';
import { SsoSVG } from './SsoSVG';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

interface TransitionStepProps {
  title: string;
  description: string;
  SvgLogo: ComponentType<SVGThemeProps>;
}

export const TransitionStep = ({ title, description, SvgLogo }: TransitionStepProps) => {
  const { theme } = useThemeUtils();
  return (
    <StyledBox>
      <SvgLogo
        brandPrimary={theme.palette.primary.main}
        brandSecondary={theme.palette.secondary.main}
        brandPrimaryBackground={theme.palette.brandBackgroundPrimary.main}
        brandSecondaryBackground={theme.palette.brandBackgroundSecondary.main}
        brandShade1={theme.palette.brandShade1.main}
        brandShade2={theme.palette.brandShade2.main}
        brandTint={theme.palette.brandTint.main}
      />
      <Typography variant="h4" color="primary" textAlign="center">
        {title}
      </Typography>
      <Typography variant="body1" color="grey.800" textAlign="center">
        {description}
      </Typography>
    </StyledBox>
  );
};

export const AccessCodeMatchedStep = ({ signUpUserType }: { signUpUserType: SignUpUserType }) => {
  const { translation } = useTranslationContext();

  return (
    <TransitionStep
      title={translation('access_code_confirmation_header')}
      description={
        signUpUserType === 'member'
          ? translation('access_code_confirmation_body')
          : translation('access_code_confirmation_body_caregiver')
      }
      SvgLogo={AccessCodeSVG}
    />
  );
};
export const WelcomeStep = () => {
  const { translation } = useTranslationContext();
  return (
    <TransitionStep
      SvgLogo={WelcomeSVG}
      title={translation('account_confirmation_header')}
      description={translation('account_confirmation_body')}
    />
  );
};
export const EligibilityConfirmedStep = ({
  signUpUserType,
}: {
  signUpUserType: SignUpUserType;
}) => {
  const { translation } = useTranslationContext();

  return (
    <TransitionStep
      title={translation('eligibility_confirmation_header')}
      description={
        signUpUserType === 'member'
          ? translation('eligibility_confirmation_body')
          : translation('eligibility_confirmation_caregiver_body')
      }
      SvgLogo={EligibilityCheckSVG}
    />
  );
};
export const ShortCutSSO = ({ hospitalDisplayName }: { hospitalDisplayName: string }) => {
  const { translation } = useTranslationContext();
  const { cobrand } = useCobrand();
  const appName = cobrand.displayName;

  return (
    <TransitionStep
      title={translation('shortcut_your_sign_up')}
      description={translation('shortcut_sign_up_text', {
        arg1: appName,
        arg2: hospitalDisplayName,
      })}
      SvgLogo={SsoSVG}
    />
  );
};
export const SSOSignUpSuccessStep = () => {
  const { translation } = useTranslationContext();

  return (
    <TransitionStep
      title={translation('eligibility_confirmation_header')}
      description={translation('sso_return_transition_text')}
      SvgLogo={EligibilityCheckSVG}
    />
  );
};
